import { Link } from "react-router-dom";
import { copy } from "@wheels-crm/shared-types";
import { useOrders } from "../hooks/useOrders";
import { useReminders } from "../hooks/useReminders";
import { Button } from "../components/ui/Button";
import { HotToggle } from "../components/orders/HotToggle";
import { ReminderBadge } from "../components/orders/ReminderBadge";
import { supabase } from "../lib/supabaseClient";

function isThisMonth(iso: string): boolean {
  const d = new Date(iso);
  const now = new Date();
  return d.getFullYear() === now.getFullYear() && d.getMonth() === now.getMonth();
}

export function DashboardPage() {
  const { orders, loading: ordersLoading } = useOrders();
  const { reminders, loading: remindersLoading, markDone } = useReminders();

  const thisMonthOrders = orders.filter((o) => isThisMonth(o.created_at));
  const stats = {
    new: thisMonthOrders.filter((o) => o.status === "new").length,
    in_progress: thisMonthOrders.filter((o) => o.status === "in_progress").length,
    done: thisMonthOrders.filter((o) => o.status === "done").length,
    rejected: thisMonthOrders.filter((o) => o.status === "rejected").length,
  };

  const hotOrders = orders.filter((o) => o.is_hot && o.status !== "done" && o.status !== "rejected");

  const twoDaysFromNow = new Date();
  twoDaysFromNow.setDate(twoDaysFromNow.getDate() + 2);
  const upcomingReminders = reminders
    .filter((r) => !r.is_done && new Date(r.due_at) <= twoDaysFromNow)
    .slice(0, 8);

  async function toggleHot(orderId: string, current: boolean) {
    await supabase.from("orders").update({ is_hot: !current }).eq("id", orderId);
  }

  return (
    <div>
      <div className="page-header">
        <h1>{copy.nav.dashboard}</h1>
        <Link to="/orders/new">
          <Button variant="primary">{copy.newOrderButton}</Button>
        </Link>
      </div>

      <div className="stat-grid">
        <div className="stat-card">
          <div className="value">{ordersLoading ? "…" : stats.new}</div>
          <div className="label">{copy.dashboard.statNew}</div>
        </div>
        <div className="stat-card">
          <div className="value">{ordersLoading ? "…" : stats.in_progress}</div>
          <div className="label">{copy.dashboard.statInProgress}</div>
        </div>
        <div className="stat-card">
          <div className="value">{ordersLoading ? "…" : stats.done}</div>
          <div className="label">{copy.dashboard.statDone}</div>
        </div>
        <div className="stat-card">
          <div className="value">{ordersLoading ? "…" : stats.rejected}</div>
          <div className="label">{copy.dashboard.statRejected}</div>
        </div>
      </div>

      <div className="widget-grid">
        <div className="card">
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.dashboard.hotWidget}</h2>
          {hotOrders.length === 0 && <p className="empty-state">{copy.emptyState}</p>}
          {hotOrders.map((o) => (
            <div key={o.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 0" }}>
              <HotToggle active={o.is_hot} onToggle={() => toggleHot(o.id, o.is_hot)} />
              <Link to={`/orders/${o.id}`}>
                {o.client.name} — {o.size_spec} {o.car_brand ?? ""}
              </Link>
            </div>
          ))}
        </div>

        <div className="card">
          <h2 style={{ fontSize: 15, marginTop: 0 }}>{copy.dashboard.remindersWidget}</h2>
          {!remindersLoading && upcomingReminders.length === 0 && (
            <p className="empty-state">{copy.emptyState}</p>
          )}
          {upcomingReminders.map((r) => (
            <div
              key={r.id}
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                gap: 8,
                padding: "6px 0",
              }}
            >
              <div>
                <Link to={`/orders/${r.order_id}`}>{r.order.client.name}</Link>
                {" — "}
                <ReminderBadge reminder={r} />
              </div>
              <Button onClick={() => markDone(r.id)}>{copy.reminders.markDone}</Button>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
